import axios from "axios";
import { getApiBase } from "./hostScope";
import { reportBackendDown, reportBackendUp } from "./backendStatus";

// In production we use same-origin (empty base → axios hits `/api/...` on the
// current host). This keeps cookies same-site (no SameSite=None needed) and
// avoids a CORS preflight on every request. In dev/preview we still honour
// REACT_APP_BACKEND_URL so localhost:3000 can target the remote preview API.
const API_BASE = getApiBase();
export const API = `${API_BASE}/api`;

const api = axios.create({
  baseURL: API,
  // Send cookies on cross-origin requests too. Required for the `.verqo.com`
  // session cookie to flow between buyers.verqo.com → sellers.verqo.com etc.
  withCredentials: true,
});

api.interceptors.request.use((cfg) => {
  // Backward-compat: legacy code paths (impersonation launch URL, magic
  // links, partner SSO) still drop tokens into localStorage. If one is
  // present, send it as a Bearer header — the backend accepts EITHER the
  // `access_token` cookie OR the Authorization header.
  const t = localStorage.getItem("token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Track backend reachability so the UI can show a "We'll be right back" screen
// during a backend crash/outage. A real HTTP response (even a 4xx) means the
// backend is up; only network errors / timeouts / 5xx / Cloudflare 52x errors
// signal that the origin is unreachable.
const BACKEND_DOWN_STATUSES = new Set([500, 502, 503, 504, 520, 521, 522, 523, 524]);
api.interceptors.response.use(
  (res) => { reportBackendUp(); return res; },
  (err) => {
    if (axios.isCancel?.(err) || err?.code === "ERR_CANCELED") return Promise.reject(err);
    const status = err?.response?.status;
    if (!err.response || BACKEND_DOWN_STATUSES.has(status)) {
      reportBackendDown();
    } else {
      reportBackendUp();
    }
    return Promise.reject(err);
  },
);

export default api;

export function formatErr(d) {
  if (d == null) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (d?.msg) return d.msg;
  return String(d);
}

export const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
